import { EyeInvisibleOutlined, EyeTwoTone, UserOutlined, MailOutlined, LockOutlined, PhoneOutlined, HomeOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input, Modal, Space, Typography, Alert, Row, Col } from "antd";
import { useEffect, useState } from "react";

import { Rule } from "antd/lib/form";
import { motion } from "framer-motion";
import { useDispatchRoot, useSelectorRoot } from "../../redux/store";
import { registerRequest } from "../../redux/controller";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { IRegisterRequest } from "../../common/login.interface";

import "./register.scss";
import { useNavigate } from "react-router-dom";

const { Title, Text, Link } = Typography;
const regexPhoneNumber = /^0(1\d{9}|3\d{8}|5\d{8}|7\d{8}|8\d{8}|9\d{8})$/;
const regexEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const regexPass = /^.{8,}$/; // Cập nhật theo yêu cầu tối thiểu 8 ký tự

const Register = () => {
    const dispatch = useDispatchRoot();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
    
    const { registerSuccess, registerError } = useSelectorRoot((state) => state.login);

    useEffect(() => {
        if (registerSuccess) {
            Modal.success({
                title: 'Đăng ký thành công!',
                content: 'Tài khoản của bạn đã được tạo thành công. Bạn có thể đăng nhập ngay bây giờ.',
                onOk: () => navigate('/login')
            });
        }
    }, [registerSuccess, navigate]);

    const onFinish = async (values: any): Promise<void> => {
        if (!acceptTerms) {
            Modal.warning({
                title: 'Thông báo',
                content: 'Vui lòng đồng ý với điều khoản sử dụng để tiếp tục.'
            });
            return;
        }

        setIsLoading(true);
        try {
            const registerData: IRegisterRequest = {
                username: values.username,
                email: values.email,
                password: values.password,
                firstname: values.firstname,
                lastname: values.lastname,
                phoneNumber: values.phoneNumber,
                address: values.address
            };
            
            dispatch(registerRequest(registerData));
        } catch (error) {
            console.error('Register error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onTermsChange = (e: CheckboxChangeEvent) => {
        setAcceptTerms(e.target.checked);
    };

    return (
        <div className='register-main'>
            <div className='register-content-area'>
                <div className='register-container'>
                    <div className='register-card'>
                        <div className='register-header'>
                            <Title level={2} className='register-title'>
                                Tạo tài khoản mới
                            </Title>
                            <Text type="secondary" className='register-subtitle'>
                                Điền thông tin để tạo tài khoản của bạn
                            </Text>
                        </div>

                        {registerError && (
                            <Alert
                                message="Đăng ký thất bại"
                                description={registerError}
                                type="error"
                                showIcon
                                closable
                                style={{ marginBottom: '24px' }}
                            />
                        )}

                        <Form
                            form={form}
                            className='register-form'
                            name="register"
                            layout="vertical"
                            onFinish={onFinish}
                            autoComplete="off"
                        >
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Họ"
                                        name="lastname"
                                        rules={[{ 
                                            required: true, 
                                            message: 'Vui lòng nhập họ!' 
                                        }]}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder="Nhập họ"
                                            size="large"
                                            className="register-input"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Tên"
                                        name="firstname"
                                        rules={[{ 
                                            required: true, 
                                            message: 'Vui lòng nhập tên!' 
                                        }]}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder="Nhập tên"
                                            size="large"
                                            className="register-input"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                label="Tên đăng nhập"
                                name="username"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập tên đăng nhập!' },
                                    { min: 3, message: 'Tên đăng nhập tối thiểu 3 ký tự!' }
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Nhập tên đăng nhập"
                                    size="large"
                                    className="register-input"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập email!' },
                                    { type: 'email', message: 'Email không hợp lệ!' }
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined />}
                                    placeholder="Nhập email"
                                    size="large"
                                    className="register-input"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Số điện thoại (tùy chọn)"
                                name="phoneNumber"
                                rules={[
                                    { 
                                        pattern: regexPhoneNumber, 
                                        message: 'Số điện thoại không hợp lệ!' 
                                    }
                                ]}
                            >
                                <Input
                                    prefix={<PhoneOutlined />}
                                    placeholder="Nhập số điện thoại"
                                    size="large"
                                    className="register-input"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Địa chỉ (tùy chọn)"
                                name="address"
                            >
                                <Input
                                    prefix={<HomeOutlined />}
                                    placeholder="Nhập địa chỉ"
                                    size="large"
                                    className="register-input"
                                />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Mật khẩu"
                                        name="password"
                                        rules={[
                                            { required: true, message: 'Vui lòng nhập mật khẩu!' },
                                            { min: 8, message: 'Mật khẩu tối thiểu 8 ký tự!' }
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined />}
                                            placeholder="Nhập mật khẩu"
                                            size="large"
                                            className="register-input"
                                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Xác nhận mật khẩu"
                                        name="confirmPassword"
                                        dependencies={['password']}
                                        rules={[
                                            { required: true, message: 'Vui lòng xác nhận mật khẩu!' },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined />}
                                            placeholder="Nhập lại mật khẩu"
                                            size="large"
                                            className="register-input"
                                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item>
                                <Checkbox 
                                    checked={acceptTerms}
                                    onChange={onTermsChange}
                                    className="terms-checkbox"
                                >
                                    Tôi đồng ý với{' '}
                                    <Link strong>Điều khoản sử dụng</Link> và{' '}
                                    <Link strong>Chính sách bảo mật</Link>
                                </Checkbox>
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    loading={isLoading}
                                    className="register-button"
                                    block
                                    disabled={!acceptTerms}
                                >
                                    {isLoading ? 'Đang tạo tài khoản...' : 'Đăng ký'}
                                </Button>
                            </Form.Item>

                            <div className='login-link'>
                                <Text type="secondary">
                                    Đã có tài khoản? {' '}
                                    <Link 
                                        strong
                                        onClick={() => navigate('/login')}
                                    >
                                        Đăng nhập ngay
                                    </Link>
                                </Text>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;